import React, { useMemo, useState } from 'react';
import HttpMethodBadge from './HttpMethodBadge';

function Sidebar({
  groupedOperations,
  selectedOperationId,
  selectedOverviewId,
  onSelectOperation,
  onSelectOverview,
  getOperationUrl,
  getGroupAnchor,
  searchTerm,
  onSearchChange
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase().replace(/\s+/g, ' ');

    return Object.entries(groupedOperations)
      .map(([group, operations]) => {
        const filteredOperations = operations.filter((operation) => {
          if (!normalizedSearch) {
            return true;
          }

          const haystack = [
            operation.method,
            operation.path,
            operation.summary,
            operation.description,
            ...(operation.tags || []),
            group
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return haystack.includes(normalizedSearch);
        });

        return [group, filteredOperations];
      })
      .filter(([, operations]) => operations.length > 0);
  }, [groupedOperations, searchTerm]);

  const hasActiveSearch = searchTerm.trim().length > 0;

  const toggleGroup = (groupName) => {
    setCollapsedGroups((previous) => ({
      ...previous,
      [groupName]: !previous[groupName]
    }));
  };

  return (
    <aside className="flex flex-col border-b border-white/10 bg-[rgba(10,15,31,0.92)] text-slate-100 lg:sticky lg:top-24 lg:h-[calc(100vh-96px)] lg:overflow-hidden lg:border-b-0 lg:border-r lg:self-start">
      <div className="sticky top-0 z-10 border-b border-white/10 bg-[rgba(10,15,31,0.96)] p-4 backdrop-blur lg:top-0">
        <input
          type="search"
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search endpoints"
          className="w-full rounded-lg border border-cyan-300/30 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-slate-400 outline-none ring-cyan-300 transition focus:ring-2"
        />
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4">
        <section>
          <h2 className="mb-2 bg-gradient-to-r from-[#00A7FF] to-[#00F7D2] bg-clip-text text-xs font-semibold uppercase tracking-[0.18em] text-transparent">Overview</h2>
          <ul className="space-y-1 text-sm text-slate-200" aria-label="API overview links">
            <li>
              <button
                type="button"
                onClick={() => onSelectOverview('authentication')}
                className={`w-full rounded-md px-2 py-1.5 text-left transition ${
                  selectedOverviewId === 'authentication'
                    ? 'bg-gradient-to-r from-[#00A7FF]/25 to-[#00F7D2]/20 text-white'
                    : 'hover:bg-white/10'
                }`}
              >
                Authentication
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => onSelectOverview('servers')}
                className={`w-full rounded-md px-2 py-1.5 text-left transition ${
                  selectedOverviewId === 'servers'
                    ? 'bg-gradient-to-r from-[#00A7FF]/25 to-[#00F7D2]/20 text-white'
                    : 'hover:bg-white/10'
                }`}
              >
                Servers
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => onSelectOverview('terms')}
                className={`w-full rounded-md px-2 py-1.5 text-left transition ${
                  selectedOverviewId === 'terms'
                    ? 'bg-gradient-to-r from-[#00A7FF]/25 to-[#00F7D2]/20 text-white'
                    : 'hover:bg-white/10'
                }`}
              >
                Terms
              </button>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-2 bg-gradient-to-r from-[#00A7FF] to-[#00F7D2] bg-clip-text text-xs font-semibold uppercase tracking-[0.18em] text-transparent">Endpoints</h2>
          <div className="space-y-3" role="list" aria-label="Endpoint groups">
            {filteredGroups.length === 0 ? (
              <p className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300">
                No endpoints found. Try another keyword.
              </p>
            ) : null}

            {filteredGroups.map(([groupName, operations]) => {
              const isCollapsed = hasActiveSearch ? false : !!collapsedGroups[groupName];

              return (
                <article id={getGroupAnchor(groupName)} key={groupName} className="overflow-hidden rounded-lg border border-cyan-300/20 bg-white/5" role="listitem">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                    aria-expanded={!isCollapsed}
                    aria-controls={`endpoint-group-${groupName}`}
                  >
                    <span className="text-sm font-semibold text-slate-100">{groupName}</span>
                    <span className="text-xs text-slate-300">{isCollapsed ? '+' : '-'}</span>
                  </button>

                  {!isCollapsed ? (
                    <ul
                      id={`endpoint-group-${groupName}`}
                      className="space-y-1 border-t border-white/10 p-2"
                    >
                      {operations.map((operation) => (
                        <li key={operation.id}>
                          <a
                            href={getOperationUrl(operation)}
                            onClick={(event) => {
                              if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                                event.preventDefault();
                                onSelectOperation(operation.id);
                              }
                            }}
                            className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition ${
                              selectedOperationId === operation.id
                                ? 'bg-gradient-to-r from-[#00A7FF]/25 to-[#00F7D2]/20 text-white'
                                : 'text-slate-200 hover:bg-white/10'
                            }`}
                          >
                            <HttpMethodBadge method={operation.method} />
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold">{operation.path}</p>
                              <p className="truncate text-[11px] text-slate-400">{operation.summary}</p>
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </aside>
  );
}

export default Sidebar;
